import { useMemo, useCallback, useEffect } from 'react';
import { useDatabase } from '@/context/DatabaseContext';
import { useAppStore } from '@/store/appStore';
import { useSpaces } from './useSpaces';
import { useSpaceItems } from './useSpaceItems';
import { useTaskEnrichment } from './useTaskEnrichment';
import { getOverdueTasks } from '@/utils/taskUtils';
import { normalizeStatus, TASK_STATUS, isCompletedStatus } from '@/constants/taskStatuses';
import { useItemNavigation } from './useItemNavigation';
import { useContextMenuActions } from './useContextMenuActions';
import { useAuth } from '@/context/AuthContext';
import { useDialogStore } from '@/store/dialogStore';

/**
* Hook principale per la Sidebar.
* Combina molteplici hook per fornire tutti i dati e le funzioni necessarie alla sidebar:
* - Spaces, folders, lists e relativi item
* - Tasks arricchiti e filtrati
* - Statistiche (overdue, today, in progress)
* - Gestione della navigazione e delle azioni del context menu
* 
* @returns Oggetto con tutti i dati e le funzioni per la sidebar
*/
export function useSidebar() {
  const { tasks: allTasks, loading } = useDatabase();
  const { spaces, categories, containersMap } = useSpaces();
  const { spaceItems, folderMappings } = useSpaceItems(spaces);

  // Filter tasks to only include those belonging to current org's spaces
  const tasks = useMemo(() => {
    if (!containersMap || containersMap.size === 0) return allTasks;
    return allTasks.filter(t => containersMap.has(t.spaceId));
  }, [allTasks, containersMap]);

  const { enrichedTasks } = useTaskEnrichment({
  tasks,
  containersMap: containersMap || new Map(),
  });

  const filters = useAppStore((state) => state.filters);
  const viewMode = useAppStore((state) => state.viewMode);
  const isMobile = useAppStore((state) => state.isMobile);
  const isMobileSidebarOpen = useAppStore((state) => state.isMobileSidebarOpen);
  const setFilters = useAppStore((state) => state.setFilters);
  const setViewMode = useAppStore((state) => state.setViewMode);
  const setMobileSidebarOpen = useAppStore((state) => state.setMobileSidebarOpen);
  const openCommandPalette = useAppStore((state) => state.openCommandPalette);
  const openUserSettings = useAppStore((state) => state.openUserSettings);
  const openProjectSettings = useAppStore((state) => state.openProjectSettings);
  const openCreateTask = useAppStore((state) => state.openCreateTask);
  const cachedSidebarStats = useAppStore((state) => state.sidebarStats);
  const setSidebarStats = useAppStore((state) => state.setSidebarStats);

  const { handleItemClick } = useItemNavigation();
  const { handleContextMenuAction } = useContextMenuActions();
  const { user } = useAuth();

  // Filter tasks assigned to the current user for inbox stats
  const myTasks = useMemo(() => {
    if (!user?.id) return [];
    return enrichedTasks.filter(t => t.assignees?.includes(user.id));
  }, [enrichedTasks, user?.id]);

  const stats = useMemo(() => {
  // Stats reflect only the current user's assigned tasks
  const overdue = getOverdueTasks(myTasks).length;

  const todayTasks = myTasks.filter(t => !isCompletedStatus(t.status)).length;

  const inProgress = myTasks.filter(t =>
      normalizeStatus(t.status) === TASK_STATUS.IN_PROGRESS
  ).length;

  return { overdue, today: todayTasks, inProgress };
  }, [myTasks]);

  // Persist stats to store for cache on next load
  useEffect(() => {
    if (enrichedTasks.length > 0) {
      setSidebarStats(stats);
    }
  }, [stats, enrichedTasks.length, setSidebarStats]);

  const dialogConfirm = useDialogStore((state) => state.confirm);
  const handleLogout = useCallback(async () => {
    const confirmed = await dialogConfirm({ title: 'Logout', message: 'Sei sicuro di voler uscire?' });
    if (confirmed) {
      window.location.reload();
    }
  }, [dialogConfirm]);

  const handleClose = useCallback(() => {
  setMobileSidebarOpen(false);
  }, [setMobileSidebarOpen]);

  return {
  spaces,
  categories,
  filters,
  viewMode,
  isMobile,
  isMobileSidebarOpen,
  totalTasks: enrichedTasks.length || spaces.reduce((sum, s) => sum + (s.taskCount || 0), 0),
  spaceItems,
  folderMappings,
  stats: enrichedTasks.length > 0 ? stats : cachedSidebarStats,
  loading,
  onFilterChange: setFilters,
  onViewModeChange: setViewMode,
  onOpenCommandPalette: openCommandPalette,
  onOpenUserSettings: openUserSettings,
  onOpenProjectSettings: openProjectSettings,
  onOpenCreateTask: openCreateTask,
  onLogout: handleLogout,
  onContextMenuAction: handleContextMenuAction,
  onItemClick: handleItemClick,
  onClose: handleClose,
  };
}

